import { useMemo, useState } from 'react';
import { AsYouType } from 'libphonenumber-js';
import { Input } from './primitives';

export interface CountryCode {
  code: string;
  dial: string;
  flag: string;
  placeholder?: string;
}

/**
 * Indicatifs proposés — les pays d'où viennent réellement les familles et
 * animateurs de la section. Belgique en tête et par défaut : c'est l'écrasante
 * majorité, et la seule mention explicitement demandée.
 */
export const COUNTRY_CODES: CountryCode[] = [
  { code: 'BE', dial: '+32', flag: '🇧🇪', placeholder: '0470 00 00 00' },
  { code: 'FR', dial: '+33', flag: '🇫🇷', placeholder: '06 12 34 56 78' },
  { code: 'LU', dial: '+352', flag: '🇱🇺', placeholder: '621 123 456' },
  { code: 'NL', dial: '+31', flag: '🇳🇱', placeholder: '06 12345678' },
  { code: 'DE', dial: '+49', flag: '🇩🇪', placeholder: '0151 12345678' },
];

const DEFAULT_DIAL = '+32';

/**
 * Formate les chiffres saisis localement selon les standards du pays sélectionné.
 */
export function formatLocalPhoneNumber(raw: string | null | undefined, countryCode: string, dial: string): string {
  const digits = (raw ?? '').replace(/\D/g, '');
  if (!digits) return '';

  if (digits.startsWith('0')) {
    return new AsYouType(countryCode as any).input(digits);
  }
  const full = new AsYouType().input(dial + digits);
  if (full.startsWith(dial)) {
    return full.slice(dial.length).trim();
  }
  return new AsYouType(countryCode as any).input(digits);
}

/**
 * Formate un numéro de téléphone pour l'URL WhatsApp (https://wa.me/[numero_formate]).
 * Règle WhatsApp : chiffres uniquement, sans '+', sans espaces, débutant par le code pays.
 * Si le numéro est au format local (commence par '0'), le '0' est remplacé par l'indicatif (32 par défaut).
 */
export function formatWhatsAppNumber(phone: string | null | undefined, defaultCountryCode = '32'): string {
  if (!phone) return '';
  let digits = phone.replace(/\D/g, '');
  if (!digits) return '';

  if (digits.startsWith('00')) {
    digits = digits.slice(2);
  } else if (digits.startsWith('0')) {
    digits = defaultCountryCode + digits.slice(1);
  } else if (!digits.startsWith(defaultCountryCode) && digits.length <= 9) {
    digits = defaultCountryCode + digits;
  }

  return digits;
}

/**
 * Sépare un numéro complet (« +32 470 00 00 00 ») en indicatif reconnu et
 * reste du numéro. Un indicatif absent de la liste (numéro local sans « + »,
 * ou pays non couvert) n'est jamais tronqué : la valeur d'origine reste
 * intacte dans le champ numéro plutôt que de perdre des chiffres en silence.
 */
function splitPhone(value?: string | null): { dial: string; local: string } {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return { dial: DEFAULT_DIAL, local: '' };
  const match = COUNTRY_CODES.find((country) => trimmed.startsWith(country.dial));
  if (match) return { dial: match.dial, local: trimmed.slice(match.dial.length).trim() };
  return { dial: DEFAULT_DIAL, local: trimmed };
}

/**
 * Numéro de téléphone avec sélecteur d'indicatif. La valeur reste une simple
 * chaîne (« +32 470 00 00 00 ») côté appelant, identique au format déjà
 * stocké — seule la saisie change, pas ce qui est enregistré.
 */
export function PhoneInput({
  value,
  onChange,
  disabled,
  autoFocus,
  'aria-label': ariaLabel,
}: {
  value?: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  'aria-label'?: string;
}) {
  const parsed = useMemo(() => splitPhone(value), [value]);
  // Un numéro vide ne porte aucune information de pays : sans cet état, choisir
  // un indicatif avant de taper le numéro serait aussitôt oublié au premier
  // rendu (`splitPhone('')` retombe toujours sur la Belgique par défaut). La
  // sélection manuelle prime tant qu'elle existe ; un `value` externe qui
  // change reprend la main dès qu'il contient lui-même un indicatif reconnu.
  const [manualDial, setManualDial] = useState<string | null>(null);
  const dial = manualDial ?? parsed.dial;
  const currentCountry = COUNTRY_CODES.find((c) => c.dial === dial) ?? COUNTRY_CODES[0]!;

  const formattedLocal = useMemo(() => {
    return formatLocalPhoneNumber(parsed.local, currentCountry.code, currentCountry.dial);
  }, [parsed.local, currentCountry]);

  const emit = (nextDial: string, nextLocal: string) => {
    onChange(nextLocal.trim() ? `${nextDial} ${nextLocal.trim()}` : '');
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    const formatted = formatLocalPhoneNumber(raw, currentCountry.code, currentCountry.dial);
    emit(dial, formatted);
  };

  return (
    <Input
      type="tel"
      value={formattedLocal}
      onChange={handleInputChange}
      disabled={disabled}
      autoFocus={autoFocus}
      aria-label={ariaLabel}
      placeholder={currentCountry.placeholder ?? '0470 00 00 00'}
      leading={
        <select
          value={dial}
          onChange={(event) => {
            const nextDial = event.target.value;
            setManualDial(nextDial);
            const nextCountry = COUNTRY_CODES.find((c) => c.dial === nextDial) ?? COUNTRY_CODES[0]!;
            const reformatted = formatLocalPhoneNumber(formattedLocal, nextCountry.code, nextCountry.dial);
            emit(nextDial, reformatted);
          }}
          disabled={disabled}
          aria-label="Indicatif du pays"
          style={{
            border: 'none',
            background: 'none',
            font: 'var(--t-subhead)',
            color: 'var(--text)',
            outline: 'none',
            cursor: disabled ? 'default' : 'pointer',
          }}
        >
          {COUNTRY_CODES.map((country) => (
            <option key={country.code} value={country.dial}>
              {country.flag} {country.dial}
            </option>
          ))}
        </select>
      }
    />
  );
}

/**
 * Numéro affiché en lien `tel:` — appel direct ou ajout aux contacts depuis un
 * smartphone. `null`/vide ne rend rien : à l'appelant d'afficher un « — ».
 */
export function PhoneLink({ value }: { value: string | null | undefined }) {
  if (!value) return null;
  const href = `tel:${value.replace(/[^\d+]/g, '')}`;
  return (
    <a
      href={href}
      style={{
        color: 'inherit',
        textDecoration: 'none',
        // Numéro d'un tenant, sans se faire compresser à zéro dans un flex.
        whiteSpace: 'nowrap',
        display: 'inline-block',
        flexShrink: 0,
      }}
    >
      {value}
    </a>
  );
}
